import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCalendar,
  FiPlus,
  FiGrid,
  FiClock,
  FiList,
  FiChevronDown,
  FiRefreshCw,
  FiFilter,
  FiSunrise,
} from "react-icons/fi";
import { useRoomCalendar } from "../../../context/RoomCalendarContext";
import CalendarMonthView from "./calendar/CalendarMonthView";
import CalendarUpcomingView from "./calendar/CalendarUpcomingView";
import CalendarCreateModal from "./calendar/CalendarCreateModal";
import CalendarEventModal from "./calendar/CalendarEventModal";
import "./CalendarTab.css";

// Event type configurations with colors
const EVENT_TYPES = {
  all: {
    label: "All Events",
    icon: FiCalendar,
    color: "#6366f1",
    bg: "#eef2ff",
  },
  meeting: {
    label: "Meetings",
    icon: FiCalendar,
    color: "#3b82f6",
    bg: "#dbeafe",
  },
  deadline: {
    label: "Deadlines",
    icon: FiClock,
    color: "#ef4444",
    bg: "#fee2e2",
  },
  presentation: {
    label: "Presentations",
    icon: FiGrid,
    color: "#8b5cf6",
    bg: "#ede9fe",
  },
  discussion: {
    label: "Discussions",
    icon: FiList,
    color: "#f59e0b",
    bg: "#fef3c7",
  },
  workshop: {
    label: "Workshops",
    icon: FiSunrise,
    color: "#10b981",
    bg: "#d1fae5",
  },
  milestone: {
    label: "Milestones",
    icon: FiCalendar,
    color: "#ec4899",
    bg: "#fce7f3",
  },
  reminder: {
    label: "Reminders",
    icon: FiClock,
    color: "#06b6d4",
    bg: "#cffafe",
  },
  other: { label: "Other", icon: FiCalendar, color: "#64748b", bg: "#f1f5f9" },
};

// View configurations
const VIEWS = [
  { id: "month", label: "Month", icon: FiGrid },
  { id: "upcoming", label: "Upcoming", icon: FiClock },
  { id: "list", label: "List", icon: FiList },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const fadeVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

// Sub-components
const CalendarHeader = ({
  onCreateClick,
  activeFilter,
  onFilterChange,
  eventCounts,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <motion.header className="rcal-header" variants={itemVariants}>
      <div className="rcal-header-left">
        <div className="rcal-header-icon">
          <FiCalendar />
        </div>
        <h1 className="rcal-title">Calendar</h1>
      </div>
      <div className="rcal-header-actions">
        <div className="rcal-filter-container">
          <motion.button
            className="rcal-filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiFilter />
            <span>Filter</span>
            <FiChevronDown
              className={`rcal-filter-chevron ${
                showFilters ? "rcal-filter-chevron--open" : ""
              }`}
            />
          </motion.button>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="rcal-filter-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {Object.entries(EVENT_TYPES).map(([key, config]) => {
                  const Icon = config.icon;
                  const count = eventCounts?.[key] || 0;
                  const isActive = activeFilter === key;
                  return (
                    <motion.button
                      key={key}
                      className={`rcal-filter-option ${
                        isActive ? "rcal-filter-option--active" : ""
                      }`}
                      onClick={() => {
                        onFilterChange(key);
                        setShowFilters(false);
                      }}
                      style={{
                        "--filter-color": config.color,
                        "--filter-bg": config.bg,
                      }}
                      whileHover={{ x: 4 }}
                    >
                      <span
                        className="rcal-filter-dot"
                        style={{ background: config.color }}
                      />
                      <Icon className="rcal-filter-icon" />
                      <span className="rcal-filter-label">{config.label}</span>
                      {key !== "all" && (
                        <span className="rcal-filter-count">{count}</span>
                      )}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <motion.button
          className="rcal-create-btn"
          onClick={onCreateClick}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiPlus />
          <span>Schedule Event</span>
        </motion.button>
      </div>
    </motion.header>
  );
};

const ViewToggle = ({ currentView, onViewChange }) => (
  <motion.div className="rcal-view-toggle" variants={itemVariants}>
    {VIEWS.map((view) => {
      const Icon = view.icon;
      const isActive = currentView === view.id;
      return (
        <motion.button
          key={view.id}
          className={`rcal-view-btn ${isActive ? "rcal-view-btn--active" : ""}`}
          onClick={() => onViewChange(view.id)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Icon />
          <span>{view.label}</span>
        </motion.button>
      );
    })}
  </motion.div>
);

// FilterBar removed - Filter is now in header

const QuickStats = ({ events }) => {
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    return {
      today: events.filter((e) => {
        const start = new Date(e.startDate);
        start.setHours(0, 0, 0, 0);
        return start.getTime() === today.getTime();
      }).length,
      tomorrow: events.filter((e) => {
        const start = new Date(e.startDate);
        start.setHours(0, 0, 0, 0);
        return start.getTime() === tomorrow.getTime();
      }).length,
      thisWeek: events.filter((e) => {
        const start = new Date(e.startDate);
        return start >= today && start < nextWeek;
      }).length,
      total: events.length,
    };
  }, [events]);

  const statItems = [
    { label: "Today", value: stats.today, color: "#3b82f6", bg: "#dbeafe" },
    {
      label: "Tomorrow",
      value: stats.tomorrow,
      color: "#8b5cf6",
      bg: "#ede9fe",
    },
    {
      label: "This Week",
      value: stats.thisWeek,
      color: "#10b981",
      bg: "#d1fae5",
    },
    { label: "Total", value: stats.total, color: "#f59e0b", bg: "#fef3c7" },
  ];

  return (
    <motion.div className="rcal-quick-stats" variants={itemVariants}>
      {statItems.map((stat, index) => (
        <motion.div
          key={stat.label}
          className="rcal-stat-card"
          style={{
            "--stat-color": stat.color,
            "--stat-bg": stat.bg,
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
        >
          <span className="rcal-stat-value">{stat.value}</span>
          <span className="rcal-stat-label">{stat.label}</span>
        </motion.div>
      ))}
    </motion.div>
  );
};

const LoadingState = () => (
  <motion.div
    className="rcal-loading"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="rcal-loading-spinner">
      <FiRefreshCw className="rcal-loading-icon" />
    </div>
    <p className="rcal-loading-text">Loading calendar...</p>
  </motion.div>
);

const EmptyState = ({ onCreateClick }) => (
  <motion.div
    className="rcal-empty"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="rcal-empty-icon">
      <FiCalendar />
    </div>
    <h3 className="rcal-empty-title">No Events Scheduled</h3>
    <p className="rcal-empty-text">
      Get started by scheduling your first event. Coordinate with your team and
      never miss an important date.
    </p>
    <motion.button
      className="rcal-empty-btn"
      onClick={onCreateClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <FiPlus />
      <span>Schedule Event</span>
    </motion.button>
  </motion.div>
);

const CalendarTab = ({ roomId, userRole }) => {
  const { events, loading, setCurrentRoomId, fetchEvents } = useRoomCalendar();
  const [view, setView] = useState("month");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Set current room ID and fetch events on mount
  useEffect(() => {
    if (roomId) {
      setCurrentRoomId(roomId);
      fetchEvents(roomId); // Fetch events when room is loaded
    }
  }, [roomId, setCurrentRoomId, fetchEvents]);

  // Calculate event counts per type
  const eventCounts = useMemo(() => {
    const counts = { all: events.length };
    Object.keys(EVENT_TYPES).forEach((type) => {
      if (type !== "all") {
        counts[type] = events.filter((e) => e.eventType === type).length;
      }
    });
    return counts;
  }, [events]);

  // Filter events
  const filteredEvents = useMemo(() => {
    let result = events;

    // Filter by type
    if (activeFilter !== "all") {
      result = result.filter((e) => e.eventType === activeFilter);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.title?.toLowerCase().includes(query) ||
          e.description?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [events, activeFilter, searchQuery]);

  const handleEventCreated = useCallback((newEvent) => {
    setShowCreateModal(false);
  }, []);

  const handleEventClick = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  const handleEventUpdate = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleRefresh = useCallback(() => {
    if (roomId) {
      fetchEvents(roomId);
    }
  }, [roomId, fetchEvents]);

  return (
    <motion.div
      className="rcal-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <CalendarHeader
        onCreateClick={() => setShowCreateModal(true)}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        eventCounts={eventCounts}
      />

      <QuickStats events={events} />

      <ViewToggle currentView={view} onViewChange={setView} />

      <div className="rcal-content">
        <AnimatePresence mode="wait">
          {loading && events.length === 0 ? (
            <LoadingState key="loading" />
          ) : filteredEvents.length === 0 && !loading ? (
            <EmptyState
              key="empty"
              onCreateClick={() => setShowCreateModal(true)}
            />
          ) : (
            <motion.div
              key={view}
              className="rcal-view-container"
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {view === "month" && (
                <CalendarMonthView
                  roomId={roomId}
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                  eventTypes={EVENT_TYPES}
                />
              )}

              {(view === "upcoming" || view === "list") && (
                <CalendarUpcomingView
                  roomId={roomId}
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                  eventTypes={EVENT_TYPES}
                  onRefresh={handleRefresh}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Create Event Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CalendarCreateModal
            roomId={roomId}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleEventCreated}
            eventTypes={EVENT_TYPES}
          />
        )}
      </AnimatePresence>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <CalendarEventModal
            event={selectedEvent}
            roomId={roomId}
            userRole={userRole}
            onClose={() => setSelectedEvent(null)}
            onUpdate={handleEventUpdate}
            eventTypes={EVENT_TYPES}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CalendarTab;
